import evaluateFieldConditions from '../evaluate-field-conditions';
import { parseIdKey } from '../ids';
import { runHiddenDependents } from './dependent-field-processing';

const uniq = arr => Array.from(new Set(arr));
const sortAlpha = arr => [...arr].sort();
const isStr = v => typeof v === 'string' && v.length > 0;
const numOrNull = v => (Number.isFinite(v) ? v : null);
const splitIdValue = optionValue => parseIdKey(optionValue);

const isActive = (field, values = {}, fieldPathIndex = {}) => {
  try {
    if (!field?.conditions) return true;
    return !!evaluateFieldConditions(field.conditions, values || {}, { fieldPathIndex });
  } catch {
    return true;
  }
};

// Check if an upload is accepted (verified, needs_review, not_applicable, or failed with >= 3 attempts)
// Excludes rejected uploads and failed uploads with < 3 attempts
// For non-ID documents (no id_upload_detail), consider them accepted if they exist and are assigned to a list
export const isAcceptedUpload = upload => {
  if (!upload) return false;

  // If there's no id_upload_detail, it's a non-ID document
  // Only consider it accepted if it has id_requirements_list in metadata (assigned to a list)
  if (!upload?.id_upload_detail) {
    const listCode = upload?.metadata?.id_requirements_list ?? null;
    // Must have id_requirements_list in metadata to be considered accepted
    return isStr(listCode);
  }

  const status = upload?.id_upload_detail?.status;
  const attempts = upload?.metadata?.attempts || 0;
  return (
    status === 'verified' ||
    status === 'needs_review' ||
    status === 'not_applicable' ||
    (status === 'failed' && attempts >= 3)
  );
};

// Check if an upload counts as verified for strict check-type requirements
// Includes verified status AND not_applicable (skip_verification documents count as passing)
export const isVerifiedUpload = upload => {
  if (!upload) return false;
  const status = upload?.id_upload_detail?.status;
  return status === 'verified' || status === 'not_applicable';
};

// Build { [idType]: [countryCode, ...] } and filter valid uploads
// Note: For dual uploads, we need to check if back_upload_id exists in id_upload_detail
// This check is done later in updateIdRequirementsGroup when we know which options are dual
export const computeSelectedIdsFromUploads = (idUploads = []) => {
  // Ensure idUploads is always an array (handles null being passed explicitly)
  const safeIdUploads = idUploads || [];

  const selectedIdTypes = {};
  const validUploads = [];
  let hasDobId = false;
  let hasPhotoId = false;
  // Also track verified versions for strict check type verification
  let verifiedHasDobId = false;
  let verifiedHasPhotoId = false;

  for (const upload of safeIdUploads) {
    const idValue = upload?.metadata?.id_type || null;
    const { idType, countryCode } = splitIdValue(idValue);
    if (!isStr(idType)) continue;

    if (!isAcceptedUpload(upload)) continue;

    // Add to valid uploads
    validUploads.push(upload);

    // Build selected types
    if (!selectedIdTypes[idType]) selectedIdTypes[idType] = [];
    if (isStr(countryCode)) selectedIdTypes[idType].push(countryCode);

    // Check extracted fields for DOB and photo info from ID verification
    const extractedFields = upload?.id_upload_detail?.extracted_fields;
    if (extractedFields?.includes_dob === true) {
      hasDobId = true;
      // Only count as verified if upload is verified or not_applicable
      if (isVerifiedUpload(upload)) {
        verifiedHasDobId = true;
      }
    }

    if (upload?.id_upload_detail?.has_photo === true) {
      hasPhotoId = true;
      // Only count as verified if upload is verified or not_applicable
      if (isVerifiedUpload(upload)) {
        verifiedHasPhotoId = true;
      }
    }
  }

  for (const [idType, countries] of Object.entries(selectedIdTypes)) {
    selectedIdTypes[idType] = sortAlpha(uniq(countries));
  }

  return {
    selectedIdTypes,
    validUploads,
    hasDobId,
    hasPhotoId,
    verifiedHasDobId,
    verifiedHasPhotoId,
  };
};

const isSelectedId = (selectedIds, idType, countryCode = null) => {
  if (!isStr(idType)) return false;
  if (countryCode) {
    const arr = selectedIds[idType] || [];
    return arr.includes(countryCode);
  }
  // Generic presence = the key exists, regardless of array contents
  return Object.prototype.hasOwnProperty.call(selectedIds, idType);
};

// Check if there's at least one verified upload matching the given idType and countryCode
// Used for strict check-type verification requirements
const hasVerifiedUploadForOption = (uploads, optIdType, optCountryCode) => {
  if (!Array.isArray(uploads)) return false;
  return uploads.some(upload => {
    if (!isVerifiedUpload(upload)) return false;
    const uploadIdValue = upload?.metadata?.id_type || null;
    const { idType: uploadIdType, countryCode: uploadCountryCode } = splitIdValue(uploadIdValue);
    if (uploadIdType !== optIdType) return false;
    if (isStr(optCountryCode)) return uploadCountryCode === optCountryCode;
    return true;
  });
};

const isMutuallyExclusiveOptSelected = (opt, selectedIds) => {
  const rules = Array.isArray(opt?.mutuallyExclusiveWith) ? opt.mutuallyExclusiveWith : [];

  if (rules.length === 0) return false;

  return rules.some(rule => {
    if (!isStr(rule)) return false;
    const { idType, countryCode } = splitIdValue(rule);
    return isSelectedId(selectedIds, idType, countryCode || null);
  });
};

const hasSpecificApplied = (appliedSet, idType) => {
  // any "idType:CC" in the set
  const prefix = `${idType}:`;
  for (const key of appliedSet) if (key.startsWith(prefix)) return true;
  return false;
};

// Check if a dual upload is complete (has both front and back)
const isDualUploadComplete = upload => {
  if (!upload) return false;
  // Check if this upload has a back_upload_id in its id_upload_detail
  const backUploadId = upload?.id_upload_detail?.back_upload_id;
  return backUploadId !== null && backUploadId !== undefined;
};

// Check if an expiry date meets the tolerance requirement
// Returns true if the expiry date is within tolerance (can be expired up to tolerance months in the past)
// Date format is DD/MM/YYYY as returned by Regula
// Example: if tolerance is 24 months, checks if expiry >= (today - 24 months)
// This means the ID can be expired by up to 24 months and still be accepted
const meetsExpiryTolerance = (idExpiry, toleranceMonths) => {
  if (!idExpiry || toleranceMonths == null) return true;

  try {
    const [day, month, year] = idExpiry.split('/').map(Number);
    if (!day || !month || !year) return true;

    // JS Date months are 0-indexed (0=Jan, 11=Dec), so subtract 1
    const expiryDate = new Date(year, month - 1, day);
    if (isNaN(expiryDate.getTime())) return true;

    const now = new Date();
    // Calculate minimum valid expiry: today - tolerance months (allows expired IDs up to tolerance months old)
    const minExpiryDate = new Date(
      now.getFullYear(),
      now.getMonth() - toleranceMonths,
      now.getDate(),
    );
    return expiryDate >= minExpiryDate;
  } catch {
    return true;
  }
};

// Check if an upload matches an option and is complete (for dual options, checks back_upload_id)
// Also checks expiry tolerance if the option requires it and the ID is from a different list
const uploadMatchesOption = (upload, opt, idType, countryCode, listCode = null) => {
  if (!upload) return false;
  const uploadIdValue = upload?.metadata?.id_type || null;
  const { idType: uploadIdType, countryCode: uploadCountryCode } = splitIdValue(uploadIdValue);

  // Check if upload matches the option's idType and countryCode
  const matchesType = uploadIdType === idType;
  const matchesCountry = countryCode ? uploadCountryCode === countryCode : true;

  if (!matchesType || !matchesCountry) return false;

  // If option has expiry tolerance requirement, check the id_expiry date
  // Only check if the ID is from a different list (cross-list reuse)
  // If the ID is from the same list, it would have already been validated during verification
  if (opt?.expiryTolerance !== null && opt?.expiryTolerance !== undefined && isStr(listCode)) {
    // Check id_upload_detail first (most common case), then fall back to metadata (for non-ID documents)
    const uploadListCode =
      upload?.id_upload_detail?.id_requirements_list ??
      upload?.metadata?.id_requirements_list ??
      null;

    // Only check expiry tolerance if the upload is from a different list
    if (uploadListCode !== listCode) {
      const idExpiry = upload?.id_upload_detail?.id_expiry;
      const toleranceMonths =
        typeof opt.expiryTolerance === 'number'
          ? opt.expiryTolerance
          : parseInt(opt.expiryTolerance, 10);

      if (!isNaN(toleranceMonths) && toleranceMonths >= 0) {
        if (!meetsExpiryTolerance(idExpiry, toleranceMonths)) {
          return false;
        }
      }
    }
  }

  // If option is dual, check that back_upload_id exists
  if (opt?.dual === true) {
    return isDualUploadComplete(upload);
  }

  return true;
};

const updateIdRequirementsGroup = (
  wrapperField,
  selectedIds,
  values = {},
  fieldPathIndex = {},
  idUploads = [],
  verifiedValues = {}, // Values with hidden fields computed from verified-only uploads
) => {
  // Ensure idUploads is always an array (handles null being passed explicitly)
  const safeIdUploads = idUploads || [];

  // Check if field is invisible and has setTrueIfInvisible prop
  const isInvisible = !isActive(wrapperField, values, fieldPathIndex);

  if (wrapperField.setTrueIfInvisible && isInvisible) {
    return {
      ...wrapperField,
      requirementsMet: true, // Requirements met because field is not applicable
    };
  }

  // For all other cases, always assess requirements normally (ignore visibility completely)
  // This includes when setTrueIfInvisible is true but field is visible (transition case)
  const groupApplied = new Set(); // keys like "passport:AU" or "passport" (generic)
  const groupContributingUploads = new Set(); // Track uploads contributing to this group
  const updatedLists = [];
  let groupCount = 0;
  let groupPoints = 0;
  // Track verified counts separately for strict check type verification
  let verifiedGroupCount = 0;
  let verifiedGroupPoints = 0;

  const lists = (wrapperField?.subFields || [])
    .map(sf => (sf?.type === 'id_requirements_list' ? sf : null))
    .filter(Boolean);

  // Build a set of list codes in this group for cross-list conflict checking
  const listCodesInGroup = new Set(lists.map(list => list.code).filter(isStr));

  // These are option values that appear in multiple lists and don't have allowCrossListCount
  const duplicateIdValues = Array.isArray(wrapperField?.duplicateIdValues)
    ? wrapperField.duplicateIdValues
    : [];

  // Build maps and pending assignment array in a single pass through uploads
  // Map structure: { [listCode]: Set of uploads }
  const uploadsByListCode = new Map();
  // Array of uploads from duplicateIdValues that are NOT already assigned via a list in the group
  const uploadsPendingAssignment = [];
  // Set for quick lookup of duplicate values (only created if duplicates exist)
  const duplicateIdValuesSet = duplicateIdValues.length > 0 ? new Set(duplicateIdValues) : null;

  safeIdUploads.forEach(upload => {
    const uploadId = upload?.id;
    if (!uploadId) return;

    // Check id_upload_detail first (most common case), then fall back to metadata (for non-ID documents)
    const uploadListCode =
      upload?.id_upload_detail?.id_requirements_list ??
      upload?.metadata?.id_requirements_list ??
      null;
    const isAssignedToGroupList = isStr(uploadListCode) && listCodesInGroup.has(uploadListCode);

    // Build uploadsByListCode for cross-list conflict checking
    if (isAssignedToGroupList) {
      if (!uploadsByListCode.has(uploadListCode)) {
        uploadsByListCode.set(uploadListCode, new Set());
      }
      uploadsByListCode.get(uploadListCode).add(upload);
    }

    // Build uploadsPendingAssignment: check if upload matches a duplicate value and is external
    if (duplicateIdValuesSet) {
      const uploadIdValue = upload?.metadata?.id_type || null;
      if (
        isStr(uploadIdValue) &&
        duplicateIdValuesSet.has(uploadIdValue) &&
        !isAssignedToGroupList
      ) {
        // This upload matches a duplicate value and is not assigned to a list in this group
        uploadsPendingAssignment.push(upload);
      }
    }
  });

  // Build a Set of pending assignment upload IDs for quick lookup during passes
  const pendingAssignmentUploadIds = new Set(
    uploadsPendingAssignment.map(upload => upload?.id).filter(Boolean),
  );

  // Helper function to check if the same ID type is uploaded in another list in this group
  const isUploadedInAnotherList = (idType, countryCode, currentListCode) => {
    if (!isStr(idType) || !isStr(currentListCode)) return false;

    // Check all other lists in this group
    for (const [listCode, uploads] of uploadsByListCode.entries()) {
      // Skip the current list
      if (listCode === currentListCode) continue;

      // Check if any upload in this other list matches the ID type
      for (const upload of uploads) {
        const uploadIdValue = upload?.metadata?.id_type || null;
        const { idType: uploadIdType, countryCode: uploadCountryCode } =
          splitIdValue(uploadIdValue);

        // Check if it matches the option's ID type and country code
        if (uploadIdType === idType) {
          // For specific options (with countryCode), must match country
          if (isStr(countryCode)) {
            if (uploadCountryCode === countryCode) {
              return true;
            }
          } else {
            // For generic options, any country matches
            return true;
          }
        }
      }
    }

    return false;
  };

  for (const list of lists) {
    // Only check visibility if setTrueIfInvisible prop is set
    if (list.setTrueIfInvisible && !isActive(list, values, fieldPathIndex)) {
      const next = { ...list };
      if (list.hasIdRequirements) {
        next.requirementsMet = true; // Requirements met because list is not applicable
      }
      updatedLists.push(next);
      continue;
    }
    // For all other cases, always assess requirements normally (ignore visibility completely)

    // Where alternative idRequirements are defined we check those conditions
    // BUT we still track contributing uploads so check-level verification can check their status
    if (list?.idRequirements) {
      let met = false;
      let verifiedMet = false;
      try {
        // Evaluate with all accepted uploads
        met = !!evaluateFieldConditions(list.idRequirements, values, { fieldPathIndex });
        // Evaluate with verified-only uploads for strict verification
        verifiedMet = !!evaluateFieldConditions(list.idRequirements, verifiedValues, {
          fieldPathIndex,
        });
      } catch {}

      // Even when using idRequirements conditions, track uploads that match this list's options
      // This allows check-level verification to validate upload statuses
      if (met) {
        for (const upload of safeIdUploads) {
          const uploadIdValue = upload?.metadata?.id_type || null;
          if (!uploadIdValue) continue;

          const { idType: uploadIdType, countryCode: uploadCountryCode } =
            splitIdValue(uploadIdValue);

          // Check if upload matches any option in this list
          const matchesOption = (list.options || []).some(opt => {
            const optIdType = opt.idType ?? null;
            const optCountryCode = opt.countryCode ?? null;

            if (uploadIdType !== optIdType) return false;
            if (isStr(optCountryCode)) {
              return uploadCountryCode === optCountryCode;
            }
            return true; // Generic option matches any country
          });

          if (matchesOption) {
            groupContributingUploads.add(upload);
          }
        }
      }

      const next = { ...list };
      if (list.hasIdRequirements) {
        next.requirementsMet = met;
        next.verifiedRequirementsMet = verifiedMet;
      }
      updatedLists.push(next);
      continue;
    }

    const listApplied = new Set();
    const listCode = list.code ?? null;

    // Check if any generic options exist in this list that would be selected
    const genericOptionsInList = (list.options || []).filter(opt => {
      const idType = opt.idType ?? null;
      const countryCode = opt.countryCode ?? null;
      return isStr(idType) && !isStr(countryCode); // Generic options have idType but no countryCode
    });

    // First pass: SPECIFIC options (TYPE:CC)
    const pass1 = (list.options || []).map(opt => {
      const idValue = opt.value ?? null;
      const idType = opt.idType ?? null;
      const countryCode = opt.countryCode ?? null;

      // Skip invalid options
      if (!isStr(idValue) || !isStr(idType)) {
        return { ...opt, selected: false, disabled: false };
      }

      const isSpecific = isStr(idType) && isStr(countryCode);
      if (!isSpecific) return { ...opt, selected: false, disabled: false }; // first pass only processes specific options

      const isSelected = isSelectedId(selectedIds, idType, countryCode);
      let selected = false;

      // Skip duplicate values only if they're external (not uploaded through any list in this group)
      // If uploaded through this list, process normally
      const isDuplicateValue = duplicateIdValues.includes(idValue);
      let shouldSkipDuplicate = false;

      if (isDuplicateValue && isSelected) {
        // Check if any matching upload is directly assigned to this list
        const validUploads = safeIdUploads.filter(upload =>
          uploadMatchesOption(upload, opt, idType, countryCode, listCode),
        );
        const hasDirectlyAssignedUpload = validUploads.some(upload => {
          // Check id_upload_detail first (most common case), then fall back to metadata (for non-ID documents)
          const uploadListCode =
            upload?.id_upload_detail?.id_requirements_list ??
            upload?.metadata?.id_requirements_list ??
            null;
          return uploadListCode === listCode;
        });

        // Only skip if no upload is directly assigned to this list
        shouldSkipDuplicate = !hasDirectlyAssignedUpload;
      }

      if (isSelected && !shouldSkipDuplicate) {
        // Find all valid matching uploads (handles dual completeness and expiry tolerance)
        const validUploads = safeIdUploads.filter(upload =>
          uploadMatchesOption(upload, opt, idType, countryCode, listCode),
        );

        if (validUploads.length === 0) {
          // Option selected but no valid upload (incomplete dual or doesn't meet expiry tolerance) - don't mark as selected
          selected = false;
        } else {
          // Skip if all matching uploads are pending assignment (will be assigned later)
          const nonPendingUploads = validUploads.filter(
            upload => !pendingAssignmentUploadIds.has(upload?.id),
          );

          // Only select if there are non-pending uploads OR if option allows cross-list counting
          if (nonPendingUploads.length > 0 || !!opt?.allowCrossListCount) {
            // if already applied in group and this option doesn't allow cross-list, don't select again
            if (!groupApplied.has(idValue) || !!opt?.allowCrossListCount) {
              selected = true;
              listApplied.add(idValue);
              groupApplied.add(idValue);

              // Track all valid uploads that match this selected option
              validUploads.forEach(upload => {
                groupContributingUploads.add(upload);
              });
            }
          }
        }
      }

      // Check if a generic option of the same type exists in this list and is selected via uploads from THIS list
      // Generic would be selected in this list if:
      // - A generic option of this type exists in the list, AND
      // - There are uploads of this type associated with THIS list AND
      // - No specific of that type is applied yet in this list
      const hasGenericInList = genericOptionsInList.some(gOpt => gOpt.idType === idType);
      let genericSelectedInThisList = false;

      if (hasGenericInList && isStr(listCode)) {
        // Check if there are uploads of this type associated with this list
        // Use uploadMatchesOption to check dual, expiry tolerance, etc.
        const hasGenericUploadsInThisList = safeIdUploads.some(upload => {
          const uploadIdValue = upload?.metadata?.id_type || null;
          const { idType: uploadIdType, countryCode: uploadCountryCode } =
            splitIdValue(uploadIdValue);
          // Check id_upload_detail first (most common case), then fall back to metadata (for non-ID documents)
          const uploadListCode =
            upload?.id_upload_detail?.id_requirements_list ??
            upload?.metadata?.id_requirements_list ??
            null;

          // Generic upload: same idType, no countryCode, and belongs to this list
          const matchesGeneric =
            uploadIdType === idType && !isStr(uploadCountryCode) && uploadListCode === listCode;

          if (!matchesGeneric) return false;

          // Use uploadMatchesOption to check dual and expiry tolerance (if from different list)
          return uploadMatchesOption(upload, opt, idType, null, listCode);
        });

        genericSelectedInThisList =
          hasGenericUploadsInThisList && !hasSpecificApplied(listApplied, idType);
      }

      // disabled if not selected AND any of these conditions are true:
      // - a mutually exclusive option is already selected, OR
      // - a generic option of the same type is selected in THIS list (via uploads from this list), OR
      // - the same ID type is already uploaded in another list in this group
      const isUploadedInOtherList = isUploadedInAnotherList(idType, countryCode, listCode);
      const disabled =
        !selected &&
        (isMutuallyExclusiveOptSelected(opt, selectedIds) ||
          genericSelectedInThisList ||
          isUploadedInOtherList);
      return { ...opt, selected, disabled };
    });

    // Second pass: GENERIC options (TYPE)
    const pass2 = pass1.map(opt => {
      const idValue = opt.value ?? null;
      const idType = opt.idType ?? null;
      const countryCode = opt.countryCode ?? null;

      // Skip invalid options
      if (!isStr(idValue) || !isStr(idType)) {
        return { ...opt, selected: false, disabled: false };
      }

      const isGeneric = isStr(idType) && !isStr(countryCode);
      if (!isGeneric) return { ...opt }; // second pass only processes generic options, selected and disabled are set in first pass

      // Compute which types now have a specific applied in this list/group
      const groupHasSpecificByType = hasSpecificApplied(groupApplied, idType);
      const listHasSpecificByType = hasSpecificApplied(listApplied, idType);

      // generic can be selected only if TYPE is present in uploads AND no specific country code of that type is applied
      const isSelected = isSelectedId(selectedIds, idType);
      let selected = false;

      // Skip duplicate values only if they're external (not uploaded through any list in this group)
      // If uploaded through this list, process normally
      const isDuplicateValue = duplicateIdValues.includes(idValue);
      let shouldSkipDuplicate = false;

      if (isDuplicateValue && isSelected && !groupHasSpecificByType && !listHasSpecificByType) {
        // Check if any matching upload is directly assigned to this list
        const validUploads = safeIdUploads.filter(upload =>
          uploadMatchesOption(upload, opt, idType, null, listCode),
        );
        const hasDirectlyAssignedUpload = validUploads.some(upload => {
          // Check id_upload_detail first (most common case), then fall back to metadata (for non-ID documents)
          const uploadListCode =
            upload?.id_upload_detail?.id_requirements_list ??
            upload?.metadata?.id_requirements_list ??
            null;
          return uploadListCode === listCode;
        });

        // Only skip if no upload is directly assigned to this list
        shouldSkipDuplicate = !hasDirectlyAssignedUpload;
      }

      if (isSelected && !groupHasSpecificByType && !listHasSpecificByType && !shouldSkipDuplicate) {
        // Find all valid matching uploads (handles dual completeness and expiry tolerance)
        const validUploads = safeIdUploads.filter(upload =>
          uploadMatchesOption(upload, opt, idType, null, listCode),
        );

        if (validUploads.length === 0) {
          // Option selected but no valid upload (incomplete dual or doesn't meet expiry tolerance) - don't mark as selected
          selected = false;
        } else {
          // Skip if all matching uploads are pending assignment (will be assigned later)
          const nonPendingUploads = validUploads.filter(
            upload => !pendingAssignmentUploadIds.has(upload?.id),
          );

          // Only select if there are non-pending uploads OR if option allows cross-list counting
          if (nonPendingUploads.length > 0 || !!opt?.allowCrossListCount) {
            if (!groupApplied.has(idValue) || !!opt?.allowCrossListCount) {
              selected = true;
              listApplied.add(idValue);
              groupApplied.add(idValue);

              // Track all valid uploads that match this selected generic option
              validUploads.forEach(upload => {
                groupContributingUploads.add(upload);
              });
            }
          }
        }
      }

      // disable only when conflict exists:
      // Disable if not selected AND any of these conditions are true:
      // - mutually exclusive option is already selected, OR
      // - specific of same type is already applied in list/group, OR
      // - already applied in group and doesn't allow cross-list counting, OR
      // - the same ID type is already uploaded in another list in this group
      const isUploadedInOtherList = isUploadedInAnotherList(idType, null, listCode);
      const disabled =
        !selected &&
        (isMutuallyExclusiveOptSelected(opt, selectedIds) ||
          listHasSpecificByType ||
          groupHasSpecificByType ||
          (groupApplied.has(idValue) && !opt?.allowCrossListCount) ||
          isUploadedInOtherList);

      return { ...opt, selected, disabled };
    });

    let count = 0;
    let points = 0;
    // Track verified counts separately for strict check type verification
    let verifiedCount = 0;
    let verifiedPoints = 0;

    pass2.forEach(o => {
      const compVal = isStr(o?.complementary) ? o.complementary : '';
      const hasComp = compVal.length > 0;

      if (hasComp) {
        const { idType: compIdType, countryCode: compCountryCode } = splitIdValue(compVal);

        o.needsComplementaryDoc =
          o.selected && !isSelectedId(selectedIds, compIdType, compCountryCode);
      } else {
        // prevent stale UI flags from previous runs
        if ('needsComplementaryDoc' in o) delete o.needsComplementaryDoc;
      }

      // don't count if not selected, or if complementary is required but not met
      if (!o.selected) return;
      if (hasComp && o.needsComplementaryDoc) return;

      // Mutually exclusive - if the mutually exclusive ids are selected through other lists they will still add to count
      // Not an issue at the moment, but may need to adjust count in the future
      const p = Number.isFinite(o.points) ? o.points : 1;
      const cv = Number.isFinite(o.countValue) ? o.countValue : 1;
      count += cv;
      points += p;
      groupCount += cv;
      groupPoints += p;

      // Check if this option has at least one verified upload for strict verification
      // Also counts not_applicable (skip_verification) uploads as passing
      const optIdType = o.idType ?? null;
      const optCountryCode = o.countryCode ?? null;
      const hasVerifiedUpload = hasVerifiedUploadForOption(
        safeIdUploads,
        optIdType,
        optCountryCode,
      );

      if (hasVerifiedUpload) {
        verifiedCount += cv;
        verifiedPoints += p;
        verifiedGroupCount += cv;
        verifiedGroupPoints += p;
      }
    });

    // Also count actual uploads assigned to this list to ensure min requirement is met
    // This is important for non-ID documents that may not have selected options but have uploads
    const uploadsAssignedToList = safeIdUploads.filter(upload => {
      if (!isAcceptedUpload(upload)) return false;

      // Check id_upload_detail first (most common case), then fall back to metadata (for non-ID documents)
      const uploadListCode =
        upload?.id_upload_detail?.id_requirements_list ??
        upload?.metadata?.id_requirements_list ??
        null;

      if (uploadListCode !== listCode) return false;

      // Check if upload matches any option in this list
      const uploadIdValue = upload?.metadata?.id_type || null;
      if (!uploadIdValue) return false;

      const { idType: uploadIdType, countryCode: uploadCountryCode } = splitIdValue(uploadIdValue);

      return pass2.some(opt => {
        const optIdType = opt.idType ?? null;
        const optCountryCode = opt.countryCode ?? null;

        if (uploadIdType !== optIdType) return false;
        if (isStr(optCountryCode)) {
          return uploadCountryCode === optCountryCode;
        }
        return true; // Generic option matches any country
      });
    });

    // Use the maximum of selected options count and actual uploads count
    // This ensures we count uploads even if options aren't selected (e.g., for non-ID documents)
    const actualUploadCount = uploadsAssignedToList.length;
    const effectiveCount = Math.max(count, actualUploadCount);

    // Calculate verified upload count for strict verification
    // Also counts not_applicable (skip_verification) uploads as passing
    const verifiedUploadsAssignedToList = uploadsAssignedToList.filter(upload =>
      isVerifiedUpload(upload),
    );
    const verifiedActualUploadCount = verifiedUploadsAssignedToList.length;
    const verifiedEffectiveCount = Math.max(verifiedCount, verifiedActualUploadCount);

    const needCount = numOrNull(list?.min || null);
    const needPoints = numOrNull(list?.minPoints || null);
    const meetsCount = needCount == null ? true : effectiveCount >= needCount;
    const meetsPoints = needPoints == null ? true : points >= needPoints;
    // Strict verification: check if requirements met using only verified uploads
    const verifiedMeetsCount = needCount == null ? true : verifiedEffectiveCount >= needCount;
    const verifiedMeetsPoints = needPoints == null ? true : verifiedPoints >= needPoints;

    const nextList = { ...list, options: pass2 };
    if (list.hasIdRequirements) {
      nextList.requirementsMet = meetsCount && meetsPoints;
      nextList.verifiedRequirementsMet = verifiedMeetsCount && verifiedMeetsPoints;
    }
    updatedLists.push(nextList);
  }

  // Assign pending uploads optimally after all passes are done
  if (uploadsPendingAssignment.length > 0) {
    // Find lists that don't meet requirements (explicitly check for false, not just falsy)
    const unmetLists = updatedLists.filter(list => {
      const isUnmet = list.hasIdRequirements && list.requirementsMet === false;
      return isUnmet;
    });

    // For each pending upload, assign it to the best list
    uploadsPendingAssignment.forEach(upload => {
      const uploadId = upload?.id;
      if (!uploadId) return;

      const uploadIdValue = upload?.metadata?.id_type || null;
      const { idType: uploadIdType, countryCode: uploadCountryCode } = splitIdValue(uploadIdValue);
      if (!isStr(uploadIdType)) return;

      let assignedListCode = null;

      if (unmetLists.length > 0) {
        // Assign to first list that doesn't meet requirements and has a matching option
        for (const list of unmetLists) {
          const listCode = list?.code;
          if (!isStr(listCode)) continue;

          // Check if this list has an option that matches the upload
          const hasMatchingOption = (list.options || []).some(opt => {
            const optIdType = opt.idType ?? null;
            const optCountryCode = opt.countryCode ?? null;

            if (uploadIdType !== optIdType) return false;
            if (isStr(optCountryCode)) {
              return uploadCountryCode === optCountryCode;
            }
            return true; // Generic option matches any country
          });

          if (hasMatchingOption) {
            assignedListCode = listCode;
            break;
          }
        }
      }

      // If no unmet list found, assign to first list that has a matching option
      if (!assignedListCode) {
        for (const list of updatedLists) {
          const listCode = list?.code;
          if (!isStr(listCode)) continue;

          const hasMatchingOption = (list.options || []).some(opt => {
            const optIdType = opt.idType ?? null;
            const optCountryCode = opt.countryCode ?? null;

            if (uploadIdType !== optIdType) return false;
            if (isStr(optCountryCode)) {
              return uploadCountryCode === optCountryCode;
            }
            return true;
          });

          if (hasMatchingOption) {
            assignedListCode = listCode;
            break;
          }
        }
      }

      // If we found a list to assign to, select the matching option and disable it in other lists
      if (assignedListCode) {
        for (let i = 0; i < updatedLists.length; i++) {
          const list = updatedLists[i];
          const listCode = list?.code;
          if (!isStr(listCode)) continue;

          // Find matching option in this list that the upload actually matches
          const matchingOptionIndex = (list.options || []).findIndex(opt => {
            const optIdType = opt.idType ?? null;
            const optCountryCode = opt.countryCode ?? null;

            if (uploadIdType !== optIdType) return false;
            if (isStr(optCountryCode)) {
              if (uploadCountryCode !== optCountryCode) return false;
            }

            // Verify upload actually matches the option (dual, expiry tolerance, etc.)
            return uploadMatchesOption(upload, opt, optIdType, optCountryCode, listCode);
          });

          if (matchingOptionIndex >= 0) {
            const opt = list.options[matchingOptionIndex];
            const idValue = opt.value ?? null;

            if (listCode === assignedListCode) {
              // Select the option in the assigned list
              if (!opt.selected && isSelectedId(selectedIds, opt.idType, opt.countryCode)) {
                list.options[matchingOptionIndex] = {
                  ...opt,
                  selected: true,
                };
                // Add to groupApplied to prevent double-selection
                groupApplied.add(idValue);

                // Update counts (both regular and verified)
                const needCount = numOrNull(list?.min || null);
                const needPoints = numOrNull(list?.minPoints || null);
                let count = 0;
                let points = 0;
                let listVerifiedCount = 0;
                let listVerifiedPoints = 0;

                list.options.forEach(o => {
                  if (o.selected) {
                    const compVal = isStr(o?.complementary) ? o.complementary : '';
                    const hasComp = compVal.length > 0;
                    if (hasComp) {
                      const { idType: compIdType, countryCode: compCountryCode } =
                        splitIdValue(compVal);
                      if (!isSelectedId(selectedIds, compIdType, compCountryCode)) return;
                    }

                    const p = Number.isFinite(o.points) ? o.points : 1;
                    const cv = Number.isFinite(o.countValue) ? o.countValue : 1;
                    count += cv;
                    points += p;

                    // Check if this option has a verified upload
                    const optIdType = o.idType ?? null;
                    const optCountryCode = o.countryCode ?? null;
                    const hasVerifiedUpload = hasVerifiedUploadForOption(
                      safeIdUploads,
                      optIdType,
                      optCountryCode,
                    );

                    if (hasVerifiedUpload) {
                      listVerifiedCount += cv;
                      listVerifiedPoints += p;
                    }
                  }
                });

                const meetsCount = needCount == null ? true : count >= needCount;
                const meetsPoints = needPoints == null ? true : points >= needPoints;
                const verifiedMeetsCount =
                  needCount == null ? true : listVerifiedCount >= needCount;
                const verifiedMeetsPoints =
                  needPoints == null ? true : listVerifiedPoints >= needPoints;

                if (list.hasIdRequirements) {
                  list.requirementsMet = meetsCount && meetsPoints;
                  list.verifiedRequirementsMet = verifiedMeetsCount && verifiedMeetsPoints;
                }

                // Update group totals
                const p = Number.isFinite(opt.points) ? opt.points : 1;
                const cv = Number.isFinite(opt.countValue) ? opt.countValue : 1;
                groupCount += cv;
                groupPoints += p;

                // Update verified group totals if this upload is verified
                if (isVerifiedUpload(upload)) {
                  verifiedGroupCount += cv;
                  verifiedGroupPoints += p;
                }
              }
            } else {
              // Disable the option in other lists
              list.options[matchingOptionIndex] = {
                ...opt,
                disabled: true,
              };
            }
          }
        }
      }
    });
  }

  // Wrapper requirements
  const wrapperMin = numOrNull(wrapperField?.min);
  const wrapperMinPoints = numOrNull(wrapperField?.minPoints);

  // Only consider lists that are active (hidden ones were pushed as met=true already)
  const allListsRequirementsMet = updatedLists.every(
    l => !l.hasIdRequirements || l.requirementsMet === true,
  );
  // Check verified requirements for strict check type verification
  const allListsVerifiedRequirementsMet = updatedLists.every(
    l => !l.hasIdRequirements || l.verifiedRequirementsMet === true,
  );

  let wrapperRequirementsMet = allListsRequirementsMet;
  let wrapperVerifiedRequirementsMet = allListsVerifiedRequirementsMet;

  if (wrapperMin != null || wrapperMinPoints != null) {
    const meetsGroupCount = wrapperMin == null ? true : groupCount >= wrapperMin;
    const meetsGroupPoints = wrapperMinPoints == null ? true : groupPoints >= wrapperMinPoints;
    wrapperRequirementsMet = allListsRequirementsMet && meetsGroupCount && meetsGroupPoints;

    // Strict verification uses verified counts
    const verifiedMeetsGroupCount = wrapperMin == null ? true : verifiedGroupCount >= wrapperMin;
    const verifiedMeetsGroupPoints =
      wrapperMinPoints == null ? true : verifiedGroupPoints >= wrapperMinPoints;
    wrapperVerifiedRequirementsMet =
      allListsVerifiedRequirementsMet && verifiedMeetsGroupCount && verifiedMeetsGroupPoints;
  } else {
    // If no min/minPoints on wrapper, just check if all lists meet their requirements
    wrapperRequirementsMet = allListsRequirementsMet;
    wrapperVerifiedRequirementsMet = allListsVerifiedRequirementsMet;
  }

  const nextWrapper = {
    ...wrapperField,
    subFields: updatedLists,
  };

  // only set on wrapper if backend had it OR wrapper has its own min/points OR any child carries the flag
  if (
    wrapperField.hasIdRequirements ||
    wrapperMin != null ||
    wrapperMinPoints != null ||
    updatedLists.some(l => l.hasIdRequirements)
  ) {
    nextWrapper.requirementsMet = wrapperRequirementsMet;
    nextWrapper.verifiedRequirementsMet = wrapperVerifiedRequirementsMet;
  }

  // Attach contributing uploads to the wrapper for tracking
  nextWrapper.contributingUploads = Array.from(groupContributingUploads);

  return nextWrapper;
};

// --- public: walk all sections and update only id_requirements fields ---
export const calculateIdRequirements = (
  sections = [],
  idUploads = [],
  values = {},
  fieldPathIndex = {},
  currentIdRequirementsMet = {},
  currentSelectedIdDocuments = {},
  hiddenFields = {},
  hiddenValues = {},
  hiddenDependencyCodes = {},
  checkTypeIdRequirements = {}, // Map of check_type_slug -> { requirements: [], requirementsMet: boolean }
) => {
  // Ensure idUploads is always an array (handles null being passed explicitly)
  const safeIdUploads = idUploads || [];

  const {
    selectedIdTypes,
    validUploads,
    hasDobId,
    hasPhotoId,
    verifiedHasDobId,
    verifiedHasPhotoId,
  } = computeSelectedIdsFromUploads(safeIdUploads);

  // Create values object with filtered uploads and selected types for hidden processors
  const valuesWithUploads = {
    ...values,
    validUploads,
    selectedIdTypes,
    hasDobId,
    hasPhotoId,
  };

  // Also create values with only verified uploads for strict check type verification
  // Also counts not_applicable (skip_verification) uploads as passing
  const verifiedUploads = validUploads.filter(u => isVerifiedUpload(u));
  const verifiedValuesWithUploads = {
    ...values,
    validUploads: verifiedUploads,
    selectedIdTypes,
    hasDobId: verifiedHasDobId,
    hasPhotoId: verifiedHasPhotoId,
  };

  // Run hidden field processors that depend on __id_uploads__
  const idUploadDependents = hiddenDependencyCodes['__id_uploads__'] || [];

  let updatedHiddenValues = { ...hiddenValues };
  let verifiedHiddenValues = { ...hiddenValues };

  if (idUploadDependents.length > 0) {
    // Run processors on all accepted uploads
    const hiddenPatch = runHiddenDependents(idUploadDependents, valuesWithUploads, hiddenFields);
    if (hiddenPatch && Object.keys(hiddenPatch).length > 0) {
      updatedHiddenValues = {
        ...updatedHiddenValues,
        ...hiddenPatch,
      };
    }

    // Run processors on verified-only uploads for strict verification
    const verifiedHiddenPatch = runHiddenDependents(
      idUploadDependents,
      verifiedValuesWithUploads,
      hiddenFields,
    );
    if (verifiedHiddenPatch && Object.keys(verifiedHiddenPatch).length > 0) {
      verifiedHiddenValues = {
        ...verifiedHiddenValues,
        ...verifiedHiddenPatch,
      };
    }
  }

  // Create final values object with updated hidden values
  const allValues = {
    ...values,
    ...updatedHiddenValues,
  };

  // Create verified values for strict check type verification
  const verifiedAllValues = {
    ...values,
    ...verifiedHiddenValues,
  };

  // Initialize tracking objects
  const idRequirementsMet = { ...currentIdRequirementsMet };
  const verifiedIdRequirementsMet = {}; // Track verified requirements met for strict check type verification
  const selectedIdDocuments = { ...currentSelectedIdDocuments };
  const requirementContributingUploads = {}; // Track uploads contributing to each requirement field code
  const updatedCheckTypeIdRequirements = { ...checkTypeIdRequirements };

  // Initialize Sets to track contributing uploads for each check type
  const checkTypeContributingUploads = {};
  for (const [checkTypeSlug, checkTypeData] of Object.entries(updatedCheckTypeIdRequirements)) {
    if (checkTypeData && typeof checkTypeData === 'object') {
      checkTypeContributingUploads[checkTypeSlug] = new Set();
    }
  }

  const updatedSections = (sections || []).map(section => ({
    ...section,
    subsections: (section?.subsections || []).map(sub => ({
      ...sub,
      fields: (sub?.fields || []).map(field => {
        if (field?.type === 'id_requirements') {
          const updatedField = updateIdRequirementsGroup(
            field,
            selectedIdTypes,
            allValues,
            fieldPathIndex,
            validUploads,
            verifiedAllValues,
          );

          // Collect requirements met status during processing
          // Only add to idRequirementsMet if the field actually has requirements
          if (field.code && updatedField.hasIdRequirements) {
            idRequirementsMet[field.code] = updatedField.requirementsMet || false;
            verifiedIdRequirementsMet[field.code] = updatedField.verifiedRequirementsMet || false;

            // Track contributing uploads for this requirement field code
            if (Array.isArray(updatedField.contributingUploads)) {
              requirementContributingUploads[field.code] = updatedField.contributingUploads;

              // If this field code is in any check type's requirements, add uploads to that check type's Set
              for (const [checkTypeSlug, checkTypeData] of Object.entries(
                updatedCheckTypeIdRequirements,
              )) {
                if (checkTypeData && typeof checkTypeData === 'object') {
                  const requirements = Array.isArray(checkTypeData.requirements)
                    ? checkTypeData.requirements
                    : [];
                  if (requirements.includes(field.code)) {
                    // Add all contributing uploads to this check type's Set
                    updatedField.contributingUploads.forEach(upload => {
                      checkTypeContributingUploads[checkTypeSlug]?.add(upload);
                    });
                  }
                }
              }
            }
          }

          // Collect selected documents during processing
          if (updatedField.subFields) {
            updatedField.subFields.forEach(subField => {
              if (subField.type === 'id_requirements_list' && subField.code) {
                const selectedOptions = (subField.options || [])
                  .filter(option => option.selected)
                  .map(option => option.value);
                selectedIdDocuments[subField.code] = selectedOptions;
              }
            });
          }

          return updatedField;
        }
        return field;
      }),
    })),
  }));

  // Calculate checkTypeIdRequirements.requirementsMet using verifiedIdRequirementsMet
  // This ensures check types use the strict verification (verified uploads only)
  for (const [checkTypeSlug, checkTypeData] of Object.entries(updatedCheckTypeIdRequirements)) {
    if (!checkTypeData || typeof checkTypeData !== 'object') continue;

    const requirements = Array.isArray(checkTypeData.requirements)
      ? checkTypeData.requirements
      : [];

    // No requirements = requirements met by default
    // Otherwise, check all requirements have verifiedRequirementsMet = true
    const requirementsMet =
      requirements.length === 0
        ? true
        : requirements.every(reqCode => verifiedIdRequirementsMet[reqCode] === true);

    updatedCheckTypeIdRequirements[checkTypeSlug] = {
      requirements,
      requirementsMet,
    };
  }

  return {
    updatedSections,
    updatedHiddenValues,
    idRequirementsMet,
    selectedIdDocuments,
    checkTypeIdRequirements: updatedCheckTypeIdRequirements,
  };
};
